/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { LocaSmartLayout } from '../../components/locasmart-layout';
import { useAppSelector } from '../../store/hooks';
import { obterIdTenent, obterIdUsuario, usuarioTemPermissao } from '../../services/seguranca';
import { TipoChavePix } from '../../services/tipos';
import {
    AfiliacaoDto,
    IndicacaoDto,
    MovimentoCarteiraDto,
    SolicitacaoSaqueDto,
    obterAfiliacao,
    gerarLink,
    excluirLink,
    listarIndicacoes,
    listarExtrato,
    listarSaques,
    solicitarSaque,
} from '../../services/afiliacao';
import {
    FaWallet, FaUsers, FaCheckCircle, FaTrophy, FaCopy, FaWhatsapp, FaPlus, FaArrowUp,
    FaShareAlt, FaUserPlus, FaMoneyBillWave, FaHourglassHalf, FaRegQuestionCircle,
} from 'react-icons/fa';

// ─── Tokens (alinhados ao dashboard_v2) ─────────────────────────────────────

const AZUL = '#1f4680';
const TINTA = '#1f2937';
const MUTED = '#64748b';
const BORDA = '#e2e8f0';
const FUNDO = '#f5f7fa';
const GRAD = 'linear-gradient(120deg, #16335f 0%, #2160b0 52%, #3b86ef 100%)';

// ─── Layout ─────────────────────────────────────────────────────────────────

const Page = styled.div`
    background: ${FUNDO};
    min-height: 100%;
    width: 100%;
    padding: 24px 28px 48px;

    @media (max-width: 640px) {
        padding: 18px 14px 40px;
    }
`;

const Group = styled.section`
    width: 100%;
    margin-bottom: 24px;
`;

const GroupTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: ${TINTA};
    margin: 0 0 14px 0;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
        content: '';
        display: block;
        width: 4px;
        height: 20px;
        background: ${GRAD};
        border-radius: 2px;
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
    width: 100%;

    @media (max-width: 1024px) { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
    @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

// ─── Card base (estilo dashboard_v2) ────────────────────────────────────────

const Card = styled.div<{ span?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 20px 22px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid ${BORDA};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    ${({ span }) => span && 'grid-column: 1 / -1;'}

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 4px;
        height: 100%;
        background: ${AZUL};
    }
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const IconWrapper = styled.div<{ tone?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    flex-shrink: 0;
    background: linear-gradient(135deg, ${({ tone }) => (tone || AZUL) + '22'} 0%, ${({ tone }) => (tone || AZUL) + '0d'} 100%);
    svg { width: 21px; height: 21px; color: ${({ tone }) => tone || AZUL}; }
`;

const CardLabel = styled.h4`
    font-size: 14px;
    font-weight: 500;
    color: ${MUTED};
    margin: 16px 0 0 0;
`;

const CardValue = styled.p`
    font-size: 26px;
    font-weight: 700;
    color: ${TINTA};
    margin: 4px 0 0 0;
    line-height: 1.1;
`;

const SectionCardTitle = styled.h3`
    font-size: 15px;
    font-weight: 600;
    color: ${TINTA};
    margin: 0;
`;

// ─── Botões ─────────────────────────────────────────────────────────────────

const Button = styled.button<{ variant?: 'primary' | 'outline' | 'whats' | 'light' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: filter 0.15s ease, transform 0.12s ease;

    background: ${({ variant }) =>
        variant === 'whats' ? '#25D366'
        : variant === 'outline' ? '#fff'
        : variant === 'light' ? '#fff'
        : GRAD};
    color: ${({ variant }) => (variant === 'outline' || variant === 'light' ? AZUL : '#fff')};
    border: 1px solid ${({ variant }) =>
        variant === 'whats' ? '#25D366'
        : variant === 'outline' ? BORDA
        : variant === 'light' ? '#fff'
        : 'transparent'};
    box-shadow: ${({ variant }) => (variant === 'light' ? '0 6px 16px rgba(0,0,0,0.16)' : 'none')};

    &:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 13px;
    color: ${MUTED};
    cursor: pointer;
    &:hover { color: #c0392b; text-decoration: underline; }
`;

// ─── Link de indicação ──────────────────────────────────────────────────────

const LinkRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 14px;
`;

const LinkInput = styled.input`
    flex: 1;
    min-width: 260px;
    padding: 12px 14px;
    border: 1px solid ${BORDA};
    border-radius: 8px;
    font-size: 14px;
    background: ${FUNDO};
    color: ${TINTA};
    outline: none;
    &:focus { border-color: ${AZUL}; }
`;

// ─── Hero (card do link, com degradê) ───────────────────────────────────────

const HeroCard = styled.div`
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    padding: 30px 32px;
    background: ${GRAD};
    color: #fff;
    box-shadow: 0 14px 30px rgba(22, 51, 95, 0.32);

    &::after {
        content: '';
        position: absolute;
        top: -60%;
        right: -10%;
        width: 360px;
        height: 360px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%);
        pointer-events: none;
    }
`;

const HeroInner = styled.div`
    position: relative;
    z-index: 1;
`;

const HeroPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: #fff;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.25);
    padding: 5px 12px;
    border-radius: 999px;
`;

const HeroTitle = styled.h2`
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    margin: 14px 0 4px 0;
`;

const HeroText = styled.p`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.88);
    margin: 0;
    max-width: 560px;
    line-height: 1.5;
`;

// ─── Topo: hero + como funciona lado a lado ─────────────────────────────────

const TopRow = styled.div`
    display: grid;
    grid-template-columns: 1.55fr 1fr;
    gap: 16px;
    align-items: stretch;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
`;

const FullCard = styled(Card)`
    height: 100%;
`;

const Steps = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 20px;
`;

const StepRow = styled.div`
    position: relative;
    display: flex;
    gap: 14px;
    padding-bottom: 22px;

    &:last-child { padding-bottom: 0; }

    &:not(:last-child)::before {
        content: '';
        position: absolute;
        left: 19px;
        top: 44px;
        bottom: 0;
        width: 2px;
        background: ${BORDA};
    }
`;

const StepIcon = styled.div`
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${GRAD};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 14px rgba(31, 70, 128, 0.26);
    svg { width: 17px; height: 17px; }
`;

const StepNum = styled.span`
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${AZUL};
    margin-bottom: 2px;
`;

const StepTitle = styled.div`
    font-size: 14.5px;
    font-weight: 600;
    color: ${TINTA};
`;

const StepDesc = styled.div`
    font-size: 13px;
    color: ${MUTED};
    margin-top: 3px;
    line-height: 1.45;
`;

// ─── Tabelas ────────────────────────────────────────────────────────────────

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
    margin-top: 14px;
`;

const Th = styled.th`
    text-align: left;
    padding: 0 12px 10px;
    color: ${MUTED};
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid ${BORDA};
`;

const Td = styled.td`
    padding: 12px;
    border-bottom: 1px solid ${BORDA};
    color: #475569;
    vertical-align: middle;
`;

const Tr = styled.tr`
    &:last-child td { border-bottom: none; }
`;

const Badge = styled.span<{ tone?: 'ok' | 'wait' | 'no' | 'info' }>`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    background: ${({ tone }) =>
        tone === 'ok' ? '#e7f4ee'
        : tone === 'no' ? '#fbecec'
        : tone === 'info' ? '#e8eef7'
        : '#fdf4e3'};
    color: ${({ tone }) =>
        tone === 'ok' ? '#1b7a4d'
        : tone === 'no' ? '#b23b3b'
        : tone === 'info' ? AZUL
        : '#9a6b16'};
`;

const Empty = styled.div`
    padding: 28px 0;
    text-align: center;
    color: ${MUTED};
    font-size: 13.5px;
`;

const ThreeCol = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    align-items: start;
    @media (max-width: 1100px) { grid-template-columns: 1fr 1fr; }
    @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const TableWrap = styled.div`
    overflow-x: auto;
`;

// ─── Modal ──────────────────────────────────────────────────────────────────

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(31, 41, 55, 0.45);
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalBox = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 28px 32px;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 12px 40px rgba(31, 41, 55, 0.2);
`;

const ModalTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: ${TINTA};
    margin: 0 0 22px 0;
`;

const FormGroup = styled.div`
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Label = styled.label`
    font-size: 13px;
    font-weight: 500;
    color: #475569;
`;

const Select = styled.select`
    padding: 10px;
    border: 1px solid ${BORDA};
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    background: #fff;
    &:focus { border-color: ${AZUL}; }
`;

const Input = styled.input`
    padding: 10px;
    border: 1px solid ${BORDA};
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    &:focus { border-color: ${AZUL}; }
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 24px;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBRL(value: number): string {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr: string): string {
    try {
        return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
        return dateStr;
    }
}

function tipoMovimentoLabel(tipo: string): string {
    switch (tipo) {
        case 'CreditoCashback': return 'Cashback';
        case 'Saque': return 'Saque';
        case 'EstornoSaque': return 'Estorno';
        default: return tipo;
    }
}

const TIPO_CHAVE_PIX_OPTIONS = [
    { value: TipoChavePix.CPF, label: 'CPF' },
    { value: TipoChavePix.CNPJ, label: 'CNPJ' },
    { value: TipoChavePix.Email, label: 'E-mail' },
    { value: TipoChavePix.Telefone, label: 'Telefone' },
    { value: TipoChavePix.Aleatoria, label: 'Aleatória (EVP)' },
];

const SALDO_MINIMO = 100;

// ─── Component ───────────────────────────────────────────────────────────────

export default function AfiliacaoPage() {
    const [token] = useState<string>(useAppSelector(state => state.token));
    const idEmpresa = obterIdTenent(token);
    const idUsuario = obterIdUsuario(token);
    const podeSacar = usuarioTemPermissao(token, 'afiliacao_sacar');

    const [afiliacao, setAfiliacao] = useState<AfiliacaoDto | null>(null);
    const [indicacoes, setIndicacoes] = useState<IndicacaoDto[]>([]);
    const [extrato, setExtrato] = useState<MovimentoCarteiraDto[]>([]);
    const [saques, setSaques] = useState<SolicitacaoSaqueDto[]>([]);
    const [carregando, setCarregando] = useState(true);

    // Modais
    const [modalSaqueAberto, setModalSaqueAberto] = useState(false);
    const [modalComoFuncionaAberto, setModalComoFuncionaAberto] = useState(false);
    const [tipoChavePix, setTipoChavePix] = useState<TipoChavePix>(TipoChavePix.CPF);
    const [chavePix, setChavePix] = useState('');
    const [valorSaque, setValorSaque] = useState<string>('');
    const [solicitando, setSolicitando] = useState(false);

    const carregar = useCallback(async () => {
        if (!idEmpresa) return;
        setCarregando(true);
        try {
            const safeGet = async <T,>(fn: () => Promise<{ data: T }>, fallback: T): Promise<T> => {
                try {
                    const res = await fn();
                    return res.data;
                } catch {
                    return fallback;
                }
            };

            const [afiliacaoData, indicacoesData, extratoData, saquesData] = await Promise.all([
                safeGet(() => obterAfiliacao(idEmpresa), null as AfiliacaoDto | null),
                safeGet(() => listarIndicacoes(idEmpresa), [] as IndicacaoDto[]),
                safeGet(() => listarExtrato(idEmpresa), [] as MovimentoCarteiraDto[]),
                safeGet(() => listarSaques(idEmpresa), [] as SolicitacaoSaqueDto[]),
            ]);

            setAfiliacao(afiliacaoData?.id ? afiliacaoData : null);
            setIndicacoes(indicacoesData ?? []);
            setExtrato(extratoData ?? []);
            setSaques(saquesData ?? []);
        } finally {
            setCarregando(false);
        }
    }, [idEmpresa]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    // ── Derivados ─────────────────────────────────────────────────────────────
    const saldo = afiliacao?.saldo ?? 0;
    const assinaram = indicacoes.filter(i => i.status === 'Assinou');
    const totalGanho = assinaram.reduce((s, i) => s + (i.valorCashback || 0), 0);
    const saquesPendentes = saques.filter(s => s.status === 'Pendente');
    const saquePendenteTotal = saquesPendentes.reduce((s, x) => s + (x.valor || 0), 0);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleGerarLink = async () => {
        try {
            await gerarLink(idEmpresa);
            toast.success('Link de afiliado gerado com sucesso!');
            carregar();
        } catch {
            toast.error('Erro ao gerar link de afiliado.');
        }
    };

    const handleCopiarLink = () => {
        if (!afiliacao?.link) return;
        navigator.clipboard.writeText(afiliacao.link)
            .then(() => toast.success('Link copiado para a área de transferência!'))
            .catch(() => toast.error('Não foi possível copiar o link.'));
    };

    const handleCompartilharWhatsApp = () => {
        if (!afiliacao?.link) return;
        const texto = `Eu uso o LocaSmart Pro pra gerir minha locadora e recomendo! Cadastre-se pelo meu link e ganhe desconto na assinatura: ${afiliacao.link}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
    };

    const handleExcluirLink = async () => {
        if (!window.confirm('Tem certeza que deseja excluir o seu link de afiliado? Todas as indicações futuras serão perdidas.')) return;
        try {
            await excluirLink(idEmpresa);
            toast.success('Link excluído com sucesso.');
            carregar();
        } catch {
            toast.error('Erro ao excluir link de afiliado.');
        }
    };

    const abrirModalSaque = () => {
        setChavePix('');
        setTipoChavePix(TipoChavePix.CPF);
        setValorSaque(afiliacao?.saldo?.toFixed(2) ?? '');
        setModalSaqueAberto(true);
    };

    const handleSolicitarSaque = async () => {
        const valor = parseFloat(valorSaque.replace(',', '.'));
        if (isNaN(valor) || valor < SALDO_MINIMO) {
            toast.error(`O valor mínimo para saque é ${formatBRL(SALDO_MINIMO)}.`);
            return;
        }
        if (afiliacao && valor > afiliacao.saldo) {
            toast.error(`O valor não pode ser maior que o saldo disponível (${formatBRL(afiliacao.saldo)}).`);
            return;
        }
        if (!chavePix.trim()) {
            toast.error('Informe a chave PIX.');
            return;
        }
        setSolicitando(true);
        try {
            await solicitarSaque(idEmpresa, idUsuario, { valor, chavePix: chavePix.trim(), tipoChavePix });
            toast.success('Solicitação de saque enviada com sucesso!');
            setModalSaqueAberto(false);
            carregar();
        } catch (err: any) {
            const mensagem = err?.response?.data?.mensagem
                || err?.response?.data?.Mensagem
                || 'Erro ao solicitar saque.';
            toast.error(mensagem);
        } finally {
            setSolicitando(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <LocaSmartLayout>
            <Page>
                {/* ── Topo: link de indicação + como funciona ── */}
                <Group>
                    <TopRow>
                        <HeroCard>
                        <HeroInner>
                            <HeroPill><FaTrophy size={12} /> 10% de cashback por indicação</HeroPill>
                            <HeroTitle>Seu link de indicação</HeroTitle>
                            <HeroText>
                                Compartilhe e ganhe 10% da primeira compra de cada empresa que assinar, sem limite de indicações.
                            </HeroText>

                            {carregando ? (
                                <HeroText style={{ marginTop: 16 }}>Carregando...</HeroText>
                            ) : afiliacao ? (
                                <>
                                    <LinkRow>
                                        <LinkInput readOnly value={afiliacao.link} onFocus={e => e.target.select()} />
                                        <Button variant="light" onClick={handleCopiarLink}><FaCopy /> Copiar</Button>
                                        <Button variant="whats" onClick={handleCompartilharWhatsApp}><FaWhatsapp /> WhatsApp</Button>
                                    </LinkRow>
                                    <div style={{ marginTop: 12 }}>
                                        <LinkButton onClick={handleExcluirLink} style={{ color: 'rgba(255,255,255,0.8)' }}>Excluir link</LinkButton>
                                    </div>
                                </>
                            ) : (
                                <LinkRow>
                                    <Button variant="light" onClick={handleGerarLink}><FaPlus /> Gerar meu link</Button>
                                </LinkRow>
                            )}

                            <div style={{ marginTop: 14 }}>
                                <button
                                    type="button"
                                    onClick={() => setModalComoFuncionaAberto(true)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 7,
                                        padding: '8px 15px', borderRadius: 999,
                                        background: 'rgba(255,255,255,0.14)',
                                        border: '1px solid rgba(255,255,255,0.4)',
                                        color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                                    }}
                                >
                                    <FaRegQuestionCircle style={{ width: 15, height: 15 }} /> Como funciona?
                                </button>
                            </div>
                        </HeroInner>
                    </HeroCard>

                        <FullCard>
                            <SectionCardTitle>Como funciona</SectionCardTitle>
                            <Steps>
                                <StepRow>
                                    <StepIcon><FaShareAlt /></StepIcon>
                                    <div>
                                        <StepNum>Passo 1</StepNum>
                                        <StepTitle>Compartilhe seu link</StepTitle>
                                        <StepDesc>Envie seu link de indicação para outras locadoras pelo WhatsApp ou redes sociais.</StepDesc>
                                    </div>
                                </StepRow>
                                <StepRow>
                                    <StepIcon><FaUserPlus /></StepIcon>
                                    <div>
                                        <StepNum>Passo 2</StepNum>
                                        <StepTitle>Eles se cadastram</StepTitle>
                                        <StepDesc>A locadora cria a conta com desconto usando o seu link.</StepDesc>
                                    </div>
                                </StepRow>
                                <StepRow>
                                    <StepIcon><FaMoneyBillWave /></StepIcon>
                                    <div>
                                        <StepNum>Passo 3</StepNum>
                                        <StepTitle>Você ganha cashback</StepTitle>
                                        <StepDesc>Quando a indicação assina um plano, 10% cai direto na sua carteira.</StepDesc>
                                    </div>
                                </StepRow>
                            </Steps>
                        </FullCard>
                    </TopRow>
                </Group>

                {/* ── Resumo (métricas) ── */}
                <Group>
                    <GroupTitle>Resumo</GroupTitle>
                    <CardsGrid>
                        <Card>
                            <CardHeader>
                                <IconWrapper><FaWallet /></IconWrapper>
                                {podeSacar && (
                                    <Button
                                        variant="outline"
                                        disabled={!afiliacao || saldo < SALDO_MINIMO}
                                        onClick={abrirModalSaque}
                                        style={{ padding: '7px 12px', fontSize: 13 }}
                                    >
                                        <FaArrowUp /> Sacar
                                    </Button>
                                )}
                            </CardHeader>
                            <CardLabel>Saldo disponível</CardLabel>
                            <CardValue>{carregando ? '...' : formatBRL(saldo)}</CardValue>
                            {podeSacar && !carregando && saldo < SALDO_MINIMO && (
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>
                                    Saldo mínimo para saque: {formatBRL(SALDO_MINIMO)}
                                    {saldo > 0 && <>, faltam {formatBRL(SALDO_MINIMO - saldo)}</>}.
                                </div>
                            )}
                        </Card>

                        <Card>
                            <CardHeader><IconWrapper><FaTrophy /></IconWrapper></CardHeader>
                            <CardLabel>Total já ganho</CardLabel>
                            <CardValue>{carregando ? '...' : formatBRL(totalGanho)}</CardValue>
                        </Card>

                        <Card>
                            <CardHeader><IconWrapper><FaHourglassHalf /></IconWrapper></CardHeader>
                            <CardLabel>Saque pendente</CardLabel>
                            <CardValue>{carregando ? '...' : formatBRL(saquePendenteTotal)}</CardValue>
                            {!carregando && saquesPendentes.length > 0 && (
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>
                                    {saquesPendentes.length} solicitaç{saquesPendentes.length > 1 ? 'ões' : 'ão'} aguardando aprovação
                                </div>
                            )}
                        </Card>

                        <Card>
                            <CardHeader><IconWrapper><FaUsers /></IconWrapper></CardHeader>
                            <CardLabel>Indicações</CardLabel>
                            <CardValue>{carregando ? '...' : indicacoes.length}</CardValue>
                        </Card>

                        <Card>
                            <CardHeader><IconWrapper><FaCheckCircle /></IconWrapper></CardHeader>
                            <CardLabel>Já assinaram</CardLabel>
                            <CardValue>{carregando ? '...' : assinaram.length}</CardValue>
                        </Card>
                    </CardsGrid>
                </Group>

                {/* ── Indicações + carteira lado a lado ── */}
                <Group>
                    <GroupTitle>Acompanhamento</GroupTitle>
                    <ThreeCol>
                        <Card>
                            <SectionCardTitle>Minhas indicações</SectionCardTitle>
                            {indicacoes.length === 0 ? (
                                <Empty>Nenhuma indicação ainda.</Empty>
                            ) : (
                                <TableWrap>
                                    <Table>
                                        <thead>
                                            <tr><Th>Empresa</Th><Th>Status</Th><Th>Cashback</Th></tr>
                                        </thead>
                                        <tbody>
                                            {indicacoes.map((ind, i) => (
                                                <Tr key={i}>
                                                    <Td style={{ color: TINTA, fontWeight: 500 }}>{ind.nomeEmpresaIndicada}</Td>
                                                    <Td>
                                                        {ind.status === 'Assinou'
                                                            ? <Badge tone="ok">Assinou</Badge>
                                                            : <Badge tone="info">Cadastrou</Badge>}
                                                    </Td>
                                                    <Td>{formatBRL(ind.valorCashback)}</Td>
                                                </Tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </TableWrap>
                            )}
                        </Card>

                        <Card>
                            <SectionCardTitle>Extrato da carteira</SectionCardTitle>
                            {extrato.length === 0 ? (
                                <Empty>Nenhum movimento ainda.</Empty>
                            ) : (
                                <TableWrap>
                                    <Table>
                                        <thead>
                                            <tr><Th>Data</Th><Th>Tipo</Th><Th>Valor</Th></tr>
                                        </thead>
                                        <tbody>
                                            {extrato.map((mov, i) => {
                                                const isCredito = mov.tipo === 'CreditoCashback' || mov.tipo === 'EstornoSaque';
                                                return (
                                                    <Tr key={i}>
                                                        <Td>{formatDate(mov.data)}</Td>
                                                        <Td>{tipoMovimentoLabel(mov.tipo)}</Td>
                                                        <Td style={{ color: isCredito ? '#1b7a4d' : '#b23b3b', fontWeight: 600 }}>
                                                            {isCredito ? '+' : '-'}{formatBRL(Math.abs(mov.valor))}
                                                        </Td>
                                                    </Tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </TableWrap>
                            )}
                        </Card>

                        <Card>
                            <SectionCardTitle>Solicitações de saque</SectionCardTitle>
                            {saques.length === 0 ? (
                                <Empty>Nenhuma solicitação ainda.</Empty>
                            ) : (
                                <TableWrap>
                                    <Table>
                                        <thead>
                                            <tr><Th>Data</Th><Th>Valor</Th><Th>Status</Th></tr>
                                        </thead>
                                        <tbody>
                                            {saques.map((saque, i) => (
                                                <Tr key={i}>
                                                    <Td>{formatDate(saque.dataSolicitacao)}</Td>
                                                    <Td>{formatBRL(saque.valor)}</Td>
                                                    <Td>
                                                        {saque.status === 'Pago' ? <Badge tone="ok">Pago</Badge>
                                                            : saque.status === 'Aprovado' ? <Badge tone="info">Aprovado</Badge>
                                                            : saque.status === 'Rejeitado' ? <Badge tone="no" title={saque.motivoRejeicao || undefined}>Rejeitado</Badge>
                                                            : saque.status === 'Falhou' ? <Badge tone="no" title={saque.motivoRejeicao || undefined}>Falhou</Badge>
                                                            : saque.status === 'Reconciliar' ? <Badge tone="wait" title={saque.motivoRejeicao || undefined}>Em reconciliação</Badge>
                                                            : <Badge tone="wait">Pendente</Badge>}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </TableWrap>
                            )}
                        </Card>
                    </ThreeCol>
                </Group>
            </Page>

            {/* ── Modal de Saque ── */}
            {modalSaqueAberto && (
                <Overlay onClick={() => !solicitando && setModalSaqueAberto(false)}>
                    <ModalBox onClick={e => e.stopPropagation()}>
                        <ModalTitle>Solicitar saque</ModalTitle>

                        <FormGroup>
                            <Label>Tipo de chave PIX</Label>
                            <Select
                                value={tipoChavePix}
                                onChange={e => setTipoChavePix(Number(e.target.value) as TipoChavePix)}
                            >
                                {TIPO_CHAVE_PIX_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Select>
                        </FormGroup>

                        <FormGroup>
                            <Label>Chave PIX</Label>
                            <Input
                                type="text"
                                placeholder="Digite a chave PIX"
                                value={chavePix}
                                onChange={e => setChavePix(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Valor (mín. {formatBRL(SALDO_MINIMO)}, máx. {formatBRL(saldo)})</Label>
                            <Input
                                type="number"
                                min={SALDO_MINIMO}
                                max={saldo}
                                step="0.01"
                                value={valorSaque}
                                onChange={e => setValorSaque(e.target.value)}
                            />
                        </FormGroup>

                        <ModalActions>
                            <Button variant="outline" onClick={() => setModalSaqueAberto(false)} disabled={solicitando}>
                                Cancelar
                            </Button>
                            <Button variant="primary" onClick={handleSolicitarSaque} disabled={solicitando}>
                                {solicitando ? 'Enviando...' : 'Confirmar saque'}
                            </Button>
                        </ModalActions>
                    </ModalBox>
                </Overlay>
            )}

            {/* ── Modal: Como funciona ── */}
            {modalComoFuncionaAberto && (
                <Overlay onClick={() => setModalComoFuncionaAberto(false)}>
                    <ModalBox onClick={e => e.stopPropagation()} style={{ maxWidth: 940, width: '94vw' }}>
                        <ModalTitle>Como funciona a indicação</ModalTitle>
                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', marginTop: 18 }}>
                            {[
                                { icon: <FaShareAlt />, num: 'Passo 1', title: 'Compartilhe seu link', desc: 'Envie seu link de indicação para outras locadoras pelo WhatsApp ou redes sociais.' },
                                { icon: <FaUserPlus />, num: 'Passo 2', title: 'O cliente se cadastra', desc: 'Ele cria a conta usando o seu link. É assim que a indicação fica registrada.' },
                                { icon: <FaCheckCircle />, num: 'Passo 3', title: 'Ele ativa um plano', desc: 'Assim que a primeira assinatura é paga, o benefício é liberado.' },
                                { icon: <FaTrophy />, num: 'Passo 4', title: 'Vocês dois ganham', desc: 'Você recebe 10% de cashback e o cliente ganha 10% de desconto na assinatura.' },
                                { icon: <FaArrowUp />, num: 'Passo 5', title: 'Você saca via PIX', desc: `Com saldo a partir de ${formatBRL(SALDO_MINIMO)}, receba direto na sua chave PIX.` },
                            ].map((s, i) => (
                                <div
                                    key={i}
                                    style={{
                                        flex: '1 1 150px', maxWidth: 172, minWidth: 138,
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        textAlign: 'center', padding: '6px 6px',
                                    }}
                                >
                                    <StepIcon style={{ width: 48, height: 48 }}>{s.icon}</StepIcon>
                                    <StepNum style={{ marginTop: 12 }}>{s.num}</StepNum>
                                    <StepTitle>{s.title}</StepTitle>
                                    <StepDesc>{s.desc}</StepDesc>
                                </div>
                            ))}
                        </div>
                        <ModalActions style={{ marginTop: 22 }}>
                            <Button variant="primary" onClick={() => setModalComoFuncionaAberto(false)}>Entendi</Button>
                        </ModalActions>
                    </ModalBox>
                </Overlay>
            )}
        </LocaSmartLayout>
    );
}
